import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/Shared";
import { SITE, REQUIREMENT_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", phone: "", email: "", requirement: "", message: "" };

const inputClass =
  "w-full rounded-sm border border-ink/20 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/40 transition-colors duration-300 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30";

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you for contacting Evaya Ventures. Your enquiry has been received and our team will get back to you shortly.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Evaya Ventures | Real Estate Development Enquiries"
        description="Contact Evaya Ventures for redevelopment opportunities, plotted land development, landowner enquiries and investment enquiries."
        path="/contact"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="pt-40 lg:pt-48" data-testid="contact-page">
        <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-12 lg:pb-32">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">Contact</p>
            <h1 className="mt-5 font-display text-5xl leading-[1.05] tracking-tight text-ink md:text-6xl lg:text-7xl" data-testid="contact-title">
              Let's Build Something Meaningful
            </h1>
            <p className="mt-6 text-base leading-relaxed text-ink/65 md:text-lg">
              Have a redevelopment opportunity, land development proposal or business enquiry? Connect with Evaya Ventures.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-10">
            <Reveal className="lg:col-span-5">
              <div className="flex h-full flex-col border border-ink/10 bg-sand p-8 lg:p-12" data-testid="contact-info-panel">
                <h2 className="font-display text-3xl text-ink">Reach us directly</h2>
                <p className="mt-4 text-base leading-relaxed text-ink/65">
                  Prefer to call or write? Use the details below and our team will respond.
                </p>
                <ul className="mt-10 space-y-8">
                  <li className="flex items-start gap-4">
                    <Phone className="mt-1 h-5 w-5 shrink-0 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Phone</p>
                      <a
                        href={SITE.phoneHref}
                        data-testid="contact-phone-link"
                        className="mt-1 block text-lg font-medium text-ink transition-colors duration-300 hover:text-brand-dark"
                      >
                        {SITE.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Mail className="mt-1 h-5 w-5 shrink-0 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Email</p>
                      <a
                        href={SITE.emailHref}
                        data-testid="contact-email-link"
                        className="mt-1 block text-lg font-medium text-ink transition-colors duration-300 hover:text-brand-dark"
                      >
                        {SITE.email}
                      </a>
                    </div>
                  </li>
                  <li>
                    <a
                      href={SITE.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-button"
                      className="inline-flex items-center gap-2.5 rounded-sm border border-ink/25 px-6 py-3.5 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-white"
                    >
                      <WhatsAppIcon className="h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  </li>
                </ul>
                <div className="mt-auto pt-12">
                  <p className="border-t border-ink/10 pt-6 text-sm leading-relaxed text-ink/55">
                    Every enquiry is read by our team. We aim to respond promptly and honestly — including when an opportunity is not the right fit.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-7">
              <form onSubmit={handleSubmit} className="border border-ink/10 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] lg:p-12" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                      Full Name
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={inputClass}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                      Phone Number
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      className={inputClass}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={inputClass}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-requirement" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                      Enquiry Type
                    </label>
                    <select
                      id="contact-requirement"
                      name="requirement"
                      required
                      value={form.requirement}
                      onChange={handleChange}
                      className={inputClass}
                      data-testid="contact-requirement-select"
                    >
                      <option value="" disabled>
                        Select your enquiry type
                      </option>
                      {REQUIREMENT_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your property, opportunity or question"
                      className={`${inputClass} resize-y`}
                      data-testid="contact-message-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="mt-8 inline-flex items-center gap-2 rounded-sm bg-brand px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      Sending
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <Send className="h-4 w-4" strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
